import { Injectable, inject, NgZone, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import { Firestore, collection, doc, query, where, orderBy, limit, onSnapshot, Timestamp, getCountFromServer, Query } from '@angular/fire/firestore';
import { Functions } from '@angular/fire/functions';
import { httpsCallable } from './telemetry';
import { BehaviorSubject, Observable } from 'rxjs';

export interface MessageReaction {
    emoji: string;
    fromPhone: string;
    reactedAt: number;
}

/**
 * Every message type the thread can receive.
 *
 * Mirrors `ConversationMessageType` in
 * `functions/src/services/whatsapp/conversations.ts`. Keep the two in step: the
 * server writes these strings and this is what reads them back.
 */
export type ConversationMessageType =
    | 'text'
    | 'template'
    | 'image'
    | 'video'
    | 'document'
    | 'audio'
    | 'sticker'
    | 'location'
    | 'contacts'
    | 'system'
    | 'unsupported';

/** A contact card shared in-thread. */
export interface SharedContactCard {
    name?: string;
    phone?: string;
}

export interface ConversationMessage {
    id: string;
    direction: 'incoming' | 'outgoing';
    type: ConversationMessageType;
    content?: string;
    templateName?: string;
    timestamp: number;
    /**
     * Outgoing only: the choices sent with an interactive button or list
     * message. `content` already holds a readable numbered version of them.
     */
    interactive?: { style: 'buttons' | 'list'; options: Array<{ id: string; title: string }> };
    status?: 'sent' | 'delivered' | 'read' | 'failed';
    sentAt?: number;
    deliveredAt?: number;
    readAt?: number;
    failedAt?: number;
    /** Why the message failed, as reported by Meta. */
    error?: string;
    errorCode?: number;
    reactions?: MessageReaction[];
    /** Set to 'sms' on every message in an SMS thread, so the bubble can say so. */
    channel?: ConversationChannel;
    /** SMS only: what the provider charged, in its credits, and the part count. */
    cost?: { credits?: number; segments?: number };
    mediaUrl?: string;
    /** MIME type reported by Meta, used for <source type>. */
    mediaMimeType?: string;
    /** Why the media could not be re-hosted. Set instead of `mediaUrl`. */
    mediaError?: string;
    /** True for a recorded voice note, as opposed to an attached audio file. */
    voice?: boolean;
    caption?: string;
    /**
     * Meta's explanation for a `type: 'unsupported'` message. Such a webhook
     * carries no body text at all, so this is the only thing there is to show.
     */
    unsupportedReason?: string;
    unsupportedCode?: number;
    /** The type Meta could not deliver, when it tells us. */
    unsupportedOriginalType?: string;
    location?: {
        latitude?: number;
        longitude?: number;
        name?: string;
        address?: string;
    };
    contactCards?: SharedContactCard[];
    sentBy?: {
        userId: string;
        email?: string;
        name?: string;
    };
}

/**
 * A campaign, scheduled send, automation or API send that went to this contact
 * outside the conversation thread. Surfaced inline so an inbound reply has the
 * context of whatever prompted it.
 */
export interface ContactSendHistoryEntry {
    campaignId: string;
    type: 'campaign' | 'scheduled' | 'automation' | 'test' | 'api';
    messageKind: 'template' | 'text';
    templateName: string;
    templateLanguage: string;
    listName: string;
    apiAppName: string;
    totalRecipients: number;
    /** First ~140 characters of the campaign body, if it had one. */
    preview: string;
    status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
    error: string;
    sentAt: number;
    deliveredAt: number | null;
    readAt: number | null;
}

export interface ConversationAssignee {
    userId: string;
    displayName: string;
    email: string;
}

export interface AssignmentHistoryEntry {
    action: 'assigned' | 'recalled' | 'dealt_with';
    userId: string;
    displayName: string;
    timestamp: number;
    fromUserId?: string;
    fromDisplayName?: string;
}

/**
 * Denormalised copy of the newest message, stored on the conversation document.
 *
 * The list renders one preview line per row. Reading it from here is what lets
 * the list be a single query of small documents instead of a subcollection read
 * per row.
 */
export interface LastMessagePreview {
    id: string;
    direction: 'incoming' | 'outgoing';
    type: ConversationMessage['type'];
    status?: ConversationMessage['status'];
    content?: string;
    templateName?: string;
    timestamp: number;
}

/**
 * Which transport a conversation runs over. Mirrors the functions-side type.
 * 'sms' threads are created when a reply arrives from the organisation's SMS
 * provider; they have no 24-hour window and replies cost provider credits.
 */
export type ConversationChannel = 'whatsapp' | 'web' | 'sms';

/**
 * Narrows the conversation list to one business number: a WhatsApp number by
 * its Meta phone number ID, or every SMS thread.
 */
export type ConversationNumberFilter = { kind: 'wa'; phoneNumberId: string } | { kind: 'sms' };

/** What the website widget's pre-chat form collected. */
export interface VisitorContact {
    firstName?: string;
    lastName?: string;
    email?: string;
    /** Digits only. Never the same field as `phoneNumber` — see the model docs. */
    mobile?: string;
    mobileConsent?: boolean;
    /** The exact wording the visitor agreed to, as it read at the time. */
    mobileConsentText?: string;
    consentAt?: number;
    consentOrigin?: string;
}

export interface Conversation {
    id: string;
    organizationId: string;
    /**
     * Where this thread lives. Absent means 'whatsapp' — the same defaulted
     * discriminator as `orgType` on the organisation. Read it through
     * `conversationChannel()` rather than comparing directly.
     */
    channel?: ConversationChannel;
    /** Web only. The widget that created this thread. */
    widgetId?: string;
    /** Web only. What the pre-chat form collected. */
    visitorContact?: VisitorContact;
    /** Web only. Where the visitor was when they started the chat. */
    visitorContext?: { pageUrl?: string; referrer?: string; locale?: string };
    /** The conversation workflow currently answering this thread, if any. */
    activeFlow?: { flowId: string; name: string; nodeId: string; since?: any };
    /** SMS only. The contact's number, digits only. Never the same field as `phoneNumber`. */
    smsPhoneNumber?: string;
    /** SMS only. Our number the contact last wrote to, digits only, when known. */
    smsBusinessNumber?: string;
    /** SMS only. Set when the person replied STOP; the composer refuses to send. */
    smsOptOut?: { at?: any; source?: string };
    /** Set by the WhatsApp handoff: the thread on the other channel. */
    linkedConversationId?: string;
    linkedChannel?: ConversationChannel;
    /**
     * Absent when the contact uses a WhatsApp username and we have had no
     * interaction with their number in the last 30 days. Use `contactLabel()`
     * rather than falling back to this directly.
     *
     * Also always absent on a website thread — a visitor's mobile number lives
     * on `visitorContact.mobile` and is deliberately kept out of this field.
     */
    phoneNumber?: string;
    /** The contact's business-scoped user ID — stable even without a number. */
    bsuid?: string;
    /** The contact's WhatsApp username, stored without a leading `@`. */
    username?: string;
    /** Tripwire only: set when this thread and another look like the same contact. */
    possibleDuplicateOf?: string;
    displayName?: string;
    profilePictureUrl?: string;
    /** Absent on a website thread, which has no Meta number to reply from. */
    phoneNumberId?: string;
    /** Absent on a website thread, which belongs to no WABA. */
    wabaId?: string;
    lastMessageAt: number;
    lastIncomingMessageAt?: number;
    lastOutgoingMessageAt?: number;
    unreadCount: number;
    /** Newest message, for the list preview. Full thread is the subcollection. */
    lastMessage?: LastMessagePreview;
    /** Total messages in the subcollection. */
    messageCount?: number;
    tags?: string[];
    archived?: boolean;
    within24HourWindow?: boolean;
    windowRemainingMs?: number;
    assignedTo?: ConversationAssignee;
    assignedAt?: number;
    assignedBy?: { userId: string; displayName: string };
    assignmentHistory?: AssignmentHistoryEntry[];
}

/**
 * Which channel a conversation runs over, with the legacy default applied.
 *
 * Every conversation created before the website widget existed has no `channel`
 * field, so this default is not a convenience — it is what keeps the entire
 * existing corpus classified correctly. Note the consequence: the default
 * cannot be expressed as a Firestore query, so channel filtering happens in
 * memory over the page the list already holds.
 */
export function conversationChannel(
    conversation: Pick<Conversation, 'channel'> | null | undefined
): ConversationChannel {
    const channel = conversation?.channel;
    return channel === 'web' || channel === 'sms' ? channel : 'whatsapp';
}

/**
 * How to name a contact, in descending order of what a reader will recognise.
 *
 * The phone number used to be the guaranteed fallback, so templates wrote
 * `displayName || phoneNumber` everywhere. It is no longer guaranteed: a
 * contact who adopts a WhatsApp username has no number outside Meta's 30-day
 * interaction window. Always returns something printable.
 */
export function contactLabel(
    contact: Pick<Conversation, 'displayName' | 'username' | 'phoneNumber' | 'bsuid'> | null | undefined
): string {
    if (!contact) return 'Unknown contact';
    if (contact.displayName) return contact.displayName;
    if (contact.username) return '@' + contact.username;
    if (contact.phoneNumber) return contact.phoneNumber;
    if (contact.bsuid) return 'WhatsApp user ' + contact.bsuid.slice(-6);
    return 'Unknown contact';
}

/** First character of the contact label, for avatar initials. Never throws. */
export function contactInitial(
    contact: Pick<Conversation, 'displayName' | 'username' | 'phoneNumber' | 'bsuid'> | null | undefined
): string {
    return contactLabel(contact).replace(/^@/, '').charAt(0).toUpperCase();
}

@Injectable({
    providedIn: 'root'
})
export class ConversationService {
    /** Messages loaded per page. Growing the window re-subscribes with a larger limit. */
    static readonly MESSAGE_PAGE_SIZE = 25;
    /** Conversations loaded per page of the list. Same growing-window approach as messages. */
    static readonly CONVERSATION_PAGE_SIZE = 100;

    private firestore = inject(Firestore);
    private injector = inject(EnvironmentInjector);
    private ngZone = inject(NgZone);
    private functions = inject(Functions);

    private readonly markReadFn = httpsCallable<any, { success: boolean }>(this.functions, 'wabMarkConversationRead');
    private readonly markAllReadFn = httpsCallable<Record<string, never>, { success: boolean; updated: number }>(this.functions, 'wabMarkAllConversationsRead');
    private readonly archiveConversationFn = httpsCallable<any, { success: boolean, archived: boolean }>(this.functions, 'wabArchiveConversation');
    private readonly sendReplyFn = httpsCallable<any, { success: boolean, messageId: string, timestamp: number }>(this.functions, 'wabSendConversationReply');
    private readonly assignConversationFn = httpsCallable<any, { success: boolean, assignedTo: ConversationAssignee }>(this.functions, 'wabAssignConversation');
    private readonly dealWithConversationFn = httpsCallable<any, { success: boolean }>(this.functions, 'wabDealWithConversation');
    private readonly recallConversationFn = httpsCallable<any, { success: boolean, assignedTo: ConversationAssignee }>(this.functions, 'wabRecallConversation');
    private readonly getAdminUsersFn = httpsCallable<any, { users: { userId: string, displayName: string, email: string, role: string }[] }>(this.functions, 'wabGetAdminUsers');
    private readonly getContactHistoryFn = httpsCallable<any, { entries: ContactSendHistoryEntry[], scanned: number }>(this.functions, 'wabGetContactMessageHistory');
    private readonly handoffToWhatsAppFn = httpsCallable<any, { success: boolean, whatsappConversationId: string, messageId: string }>(this.functions, 'wabHandoffWebConversationToWhatsApp');

    private conversationsSubject = new BehaviorSubject<Conversation[]>([]);
    public conversations$ = this.conversationsSubject.asObservable();

    private selectedConversationSubject = new BehaviorSubject<Conversation | null>(null);
    public selectedConversation$ = this.selectedConversationSubject.asObservable();

    private messagesSubject = new BehaviorSubject<ConversationMessage[]>([]);
    /** The loaded window of the open thread, oldest-first. */
    public messages$ = this.messagesSubject.asObservable();

    private hasOlderMessagesSubject = new BehaviorSubject<boolean>(false);
    public hasOlderMessages$ = this.hasOlderMessagesSubject.asObservable();

    private loadingOlderSubject = new BehaviorSubject<boolean>(false);
    public loadingOlder$ = this.loadingOlderSubject.asObservable();

    private hasMoreConversationsSubject = new BehaviorSubject<boolean>(false);
    public hasMoreConversations$ = this.hasMoreConversationsSubject.asObservable();

    private loadingMoreConversationsSubject = new BehaviorSubject<boolean>(false);
    public loadingMoreConversations$ = this.loadingMoreConversationsSubject.asObservable();

    /**
     * How many conversations match the list query in total, not just the loaded
     * window. Null until the first count comes back.
     */
    private conversationsTotalSubject = new BehaviorSubject<number | null>(null);
    public conversationsTotal$ = this.conversationsTotalSubject.asObservable();

    private conversationsUnsubscribe?: () => void;
    private selectedConversationUnsubscribe?: () => void;
    private messagesUnsubscribe?: () => void;

    /** Context for re-subscribing when the window grows. */
    private messagesContext?: { organizationId: string; conversationId: string };
    private messagesWindowSize = ConversationService.MESSAGE_PAGE_SIZE;

    /** Context for re-subscribing when the conversation list window grows. */
    private conversationsContext?: {
        organizationId: string;
        archived: boolean;
        assignedToUserId?: string;
        direction: 'asc' | 'desc';
        numberFilter?: ConversationNumberFilter | null;
    };
    private conversationsWindowSize = ConversationService.CONVERSATION_PAGE_SIZE;
    private countTimer?: ReturnType<typeof setTimeout>;

    constructor() { }

    /**
     * Convert a Firestore Timestamp (or seconds-based object) to epoch milliseconds.
     */
    private toMillis(ts: any): number {
        if (!ts) return 0;
        if (ts instanceof Timestamp) return ts.toMillis();
        if (typeof ts.toMillis === 'function') return ts.toMillis();
        if (ts.seconds) return ts.seconds * 1000;
        if (typeof ts === 'number') return ts;
        return 0;
    }

    /**
     * Compute 24-hour window fields from raw Firestore data.
     *
     * Mirrors isWithin24HourWindow() / get24HourWindowRemaining() server-side —
     * change both together.
     *
     * A website thread reports the window as open with zero time remaining, and
     * that pairing is doing real work: every countdown badge, expiry warning and
     * composer guard in this app already renders only when the window is open
     * AND the remaining time is above zero, so all of them fall silent for web
     * threads without a single template needing to know about channels.
     *
     * Note web threads DO keep `lastIncomingMessageAt` current — the unattended
     * conversation reminder reads it — so the channel must be checked first
     * rather than relying on the field being absent.
     */
    private compute24HourWindow(data: any): { within24HourWindow: boolean; windowRemainingMs: number } {
        // Neither a website thread nor an SMS thread has Meta's window.
        if (data?.channel === 'web' || data?.channel === 'sms') return { within24HourWindow: true, windowRemainingMs: 0 };

        const lastIncoming = this.toMillis(data.lastIncomingMessageAt);
        if (!lastIncoming) return { within24HourWindow: false, windowRemainingMs: 0 };

        const remaining = (24 * 60 * 60 * 1000) - (Date.now() - lastIncoming);
        return {
            within24HourWindow: remaining > 0,
            windowRemainingMs: remaining > 0 ? remaining : 0
        };
    }

    /**
     * Convert one raw message document into a ConversationMessage.
     *
     * Split out from docToConversation when messages moved to a subcollection:
     * the conversation listener and the messages listener both need it, and it
     * now runs once per message that actually changed rather than over an entire
     * thread on every snapshot.
     */
    private docToMessage(id: string, data: any): ConversationMessage {
        return {
            ...data,
            id: data.id || id,
            timestamp: this.toMillis(data.timestamp),
            sentAt: this.toMillis(data.sentAt),
            deliveredAt: this.toMillis(data.deliveredAt),
            readAt: this.toMillis(data.readAt),
            failedAt: this.toMillis(data.failedAt),
            reactions: (data.reactions || []).map((r: any) => ({
                ...r,
                reactedAt: this.toMillis(r.reactedAt)
            }))
        } as ConversationMessage;
    }

    /**
     * Convert raw Firestore document data into a Conversation object with millisecond timestamps.
     */
    private docToConversation(id: string, data: any): Conversation {
        const window = this.compute24HourWindow(data);
        return {
            ...data,
            id,
            lastMessageAt: this.toMillis(data.lastMessageAt),
            lastIncomingMessageAt: this.toMillis(data.lastIncomingMessageAt),
            lastOutgoingMessageAt: this.toMillis(data.lastOutgoingMessageAt),
            assignedAt: this.toMillis(data.assignedAt),
            // The legacy embedded array is deliberately not carried across —
            // messages come from the subcollection. Documents written before the
            // migration may still have the field; ignoring it here keeps a single
            // code path.
            messages: undefined,
            lastMessage: data.lastMessage ? {
                ...data.lastMessage,
                timestamp: this.toMillis(data.lastMessage.timestamp)
            } : undefined,
            assignmentHistory: (data.assignmentHistory || []).map((entry: any) => ({
                ...entry,
                timestamp: this.toMillis(entry.timestamp)
            })),
            within24HourWindow: window.within24HourWindow,
            windowRemainingMs: window.windowRemainingMs
        } as Conversation;
    }

    /**
     * Subscribe to real-time conversation list updates via Firestore onSnapshot.
     *
     * Loads one page; `loadMoreConversations()` grows the window. `direction`
     * orders by last activity — 'asc' puts the longest-waiting threads first.
     */
    subscribeToConversations(
        organizationId: string,
        archived: boolean = false,
        assignedToUserId?: string,
        direction: 'asc' | 'desc' = 'desc',
        numberFilter: ConversationNumberFilter | null = null
    ): void {
        this.conversationsContext = { organizationId, archived, assignedToUserId, direction, numberFilter };
        this.conversationsWindowSize = ConversationService.CONVERSATION_PAGE_SIZE;
        this.hasMoreConversationsSubject.next(false);
        this.conversationsTotalSubject.next(null);
        this.attachConversationsListener();
    }

    /**
     * Widen the list window by one page. Resolves once the larger window has
     * been delivered.
     */
    loadMoreConversations(): Promise<void> {
        if (!this.conversationsContext || !this.hasMoreConversationsSubject.value) {
            return Promise.resolve();
        }
        if (this.loadingMoreConversationsSubject.value) return Promise.resolve();

        this.loadingMoreConversationsSubject.next(true);
        this.conversationsWindowSize += ConversationService.CONVERSATION_PAGE_SIZE;
        return this.attachConversationsListener();
    }

    /**
     * The list query without its limit, so the count can share it.
     *
     * With the `!=` filter Firestore requires `archived` as the first orderBy,
     * and it runs opposite to `lastMessageAt`. Firestore matches indexes on exact
     * directions, so this must stay in step with firestore.indexes.json:
     *   newest first = archived ASC,  lastMessageAt DESC (the original index)
     *   oldest first = archived DESC, lastMessageAt ASC
     * The number filter adds an equality on `phoneNumberId` or `channel`, which
     * has its own copies of both shapes (with and without `assignedTo.userId`).
     * The archived view reuses them: an equality field's direction is ignored.
     */
    private conversationsBaseQuery(): Query | null {
        const context = this.conversationsContext;
        if (!context) return null;

        const conversationsRef = collection(
            this.firestore,
            `organizations/${context.organizationId}/whatsappConversations`
        );
        const dir = context.direction;
        const archivedDir = dir === 'desc' ? 'asc' : 'desc';
        const filter = context.numberFilter;
        const numberWhere = !filter ? [] :
            filter.kind === 'sms' ? [where('channel', '==', 'sms')] : [where('phoneNumberId', '==', filter.phoneNumberId)];

        if (context.archived) {
            return query(conversationsRef, ...numberWhere, where('archived', '==', true), orderBy('lastMessageAt', dir));
        }
        if (context.assignedToUserId) {
            return query(conversationsRef, where('assignedTo.userId', '==', context.assignedToUserId), ...numberWhere, where('archived', '!=', true), orderBy('archived', archivedDir), orderBy('lastMessageAt', dir));
        }
        return query(conversationsRef, ...numberWhere, where('archived', '!=', true), orderBy('archived', archivedDir), orderBy('lastMessageAt', dir));
    }

    /**
     * (Re)attach the list listener at the current window size.
     * @returns a promise resolving on the first snapshot of the new window.
     */
    private attachConversationsListener(): Promise<void> {
        const base = this.conversationsBaseQuery();
        if (!base) return Promise.resolve();

        this.conversationsUnsubscribe?.();
        const windowSize = this.conversationsWindowSize;

        return new Promise<void>((resolve) => {
            let settled = false;
            const settle = () => {
                if (settled) return;
                settled = true;
                this.loadingMoreConversationsSubject.next(false);
                resolve();
            };

            const q = query(base, limit(windowSize));
            this.conversationsUnsubscribe = runInInjectionContext(this.injector, () =>
                onSnapshot(q,
                    (snapshot) => {
                        const conversations = snapshot.docs.map(doc =>
                            this.docToConversation(doc.id, doc.data())
                        );
                        // A full window means there is probably more beyond it.
                        const hasMore = snapshot.size >= windowSize;
                        this.ngZone.run(() => {
                            this.conversationsSubject.next(conversations);
                            this.hasMoreConversationsSubject.next(hasMore);
                            if (!hasMore) {
                                // Everything is loaded, so the window is the total.
                                this.conversationsTotalSubject.next(snapshot.size);
                            }
                            settle();
                        });
                        if (hasMore) this.scheduleConversationCount();
                    },
                    (error) => {
                        console.error('Error in conversations snapshot:', error);
                        this.ngZone.run(() => {
                            this.conversationsSubject.next([]);
                            this.hasMoreConversationsSubject.next(false);
                            settle();
                        });
                    }
                )
            );
        });
    }

    /**
     * Refresh the total behind the list, debounced. The snapshot fires on every
     * message to any loaded thread; a count per event would be wasted reads.
     */
    private scheduleConversationCount(): void {
        clearTimeout(this.countTimer);
        this.countTimer = setTimeout(async () => {
            const context = this.conversationsContext;
            const base = this.conversationsBaseQuery();
            if (!base) return;
            try {
                const snapshot = await runInInjectionContext(this.injector, () => getCountFromServer(base));
                // Ignore a count for a query that has since been replaced.
                if (this.conversationsContext !== context) return;
                this.ngZone.run(() => this.conversationsTotalSubject.next(snapshot.data().count));
            } catch (error) {
                console.error('Error counting conversations:', error);
            }
        }, 1500);
    }

    /**
     * Subscribe to real-time updates for a single conversation via Firestore onSnapshot.
     *
     * Also opens the thread listener. The two are always wanted together, and
     * pairing them here means a caller cannot open a conversation and forget the
     * messages — which, since the split, would render an empty thread.
     */
    subscribeToConversation(organizationId: string, conversationId: string): void {
        // Tear down any existing listener
        this.selectedConversationUnsubscribe?.();

        this.subscribeToMessages(organizationId, conversationId);

        const conversationRef = doc(
            this.firestore,
            `organizations/${organizationId}/whatsappConversations/${conversationId}`
        );

        this.selectedConversationUnsubscribe = runInInjectionContext(this.injector, () =>
            onSnapshot(conversationRef,
                (snapshot) => {
                    // Emit null when the document is missing rather than staying
                    // silent — callers that open a conversation by id they did not
                    // get from the list (a notification deep link, say) would
                    // otherwise wait on a load that never completes.
                    const conversation = snapshot.exists() ?
                        this.docToConversation(snapshot.id, snapshot.data()) :
                        null;
                    this.ngZone.run(() => this.selectedConversationSubject.next(conversation));
                },
                (error) => {
                    console.error('Error in conversation snapshot:', error);
                }
            )
        );
    }

    /**
     * Subscribe to the open thread's messages.
     *
     * Ordered newest-first so the limit keeps the most recent page, then reversed
     * for display. Growing the window re-subscribes with a larger limit rather
     * than stitching a live tail onto static older pages — the whole loaded
     * thread stays live, and "are there older messages" stays a simple check on
     * whether the window came back full.
     */
    subscribeToMessages(organizationId: string, conversationId: string): void {
        this.messagesContext = { organizationId, conversationId };
        this.messagesWindowSize = ConversationService.MESSAGE_PAGE_SIZE;
        this.messagesSubject.next([]);
        this.hasOlderMessagesSubject.next(false);
        this.attachMessagesListener();
    }

    /**
     * Widen the loaded window by one page. Resolves once the larger window has
     * been delivered, so callers can restore scroll position afterwards.
     */
    loadOlderMessages(): Promise<void> {
        if (!this.messagesContext || !this.hasOlderMessagesSubject.value) {
            return Promise.resolve();
        }
        if (this.loadingOlderSubject.value) return Promise.resolve();

        this.loadingOlderSubject.next(true);
        this.messagesWindowSize += ConversationService.MESSAGE_PAGE_SIZE;
        return this.attachMessagesListener();
    }

    /**
     * (Re)attach the thread listener at the current window size.
     * @returns a promise resolving on the first snapshot of the new window.
     */
    private attachMessagesListener(): Promise<void> {
        const context = this.messagesContext;
        if (!context) return Promise.resolve();

        this.messagesUnsubscribe?.();

        return new Promise<void>((resolve) => {
            let settled = false;
            const settle = () => {
                if (settled) return;
                settled = true;
                this.loadingOlderSubject.next(false);
                resolve();
            };

            const messagesRef = collection(
                this.firestore,
                `organizations/${context.organizationId}/whatsappConversations/${context.conversationId}/messages`
            );
            const q = query(
                messagesRef,
                orderBy('timestamp', 'desc'),
                limit(this.messagesWindowSize)
            );

            this.messagesUnsubscribe = runInInjectionContext(this.injector, () =>
                onSnapshot(q,
                    (snapshot) => {
                        // A full window means Firestore had at least as many as we
                        // asked for, so there is probably more above it.
                        const hasOlder = snapshot.size >= this.messagesWindowSize;
                        const messages = snapshot.docs
                            .map((d) => this.docToMessage(d.id, d.data()))
                            .reverse();

                        this.ngZone.run(() => {
                            this.messagesSubject.next(messages);
                            this.hasOlderMessagesSubject.next(hasOlder);
                            settle();
                        });
                    },
                    (error) => {
                        console.error('Error in messages snapshot:', error);
                        this.ngZone.run(settle);
                    }
                )
            );
        });
    }

    /**
     * Mark a conversation as read
     */
    async markAsRead(organizationId: string, conversationId: string): Promise<void> {
        try {
            await this.markReadFn({ organizationId, conversationId });
        } catch (error) {
            console.error('Error marking conversation as read:', error);
            throw error;
        }
    }

    /**
     * Clear the unread count on every conversation in the organisation.
     *
     * Organisation-wide, like reading a single conversation already is — the
     * server derives the organisation from the caller rather than trusting it.
     */
    async markAllAsRead(): Promise<{ success: boolean; updated: number }> {
        const result = await this.markAllReadFn({});
        return result.data;
    }

    /**
     * Archive or unarchive a conversation
     */
    async toggleArchive(organizationId: string, conversationId: string, archived: boolean): Promise<void> {
        try {
            await this.archiveConversationFn({ organizationId, conversationId, archived });
        } catch (error) {
            console.error('Error archiving conversation:', error);
            throw error;
        }
    }

    /**
     * Send a reply in an existing conversation
     *
     * `phoneNumberId` overrides the server's own resolution with the identity
     * the user picked. Pass it only when they actually chose — omitted, the
     * server resolves from the conversation, which is right whenever the number
     * it started on is still connected.
     */
    async sendReply(
        organizationId: string,
        conversationId: string,
        messageText: string,
        phoneNumberId?: string
    ): Promise<any> {
        try {
            const result = await this.sendReplyFn({
                organizationId,
                conversationId,
                messageText,
                ...(phoneNumberId ? { phoneNumberId } : {})
            });
            return result.data;
        } catch (error) {
            console.error('Error sending reply:', error);
            throw error;
        }
    }

    /**
     * Assign a conversation to a user
     */
    async assignConversation(organizationId: string, conversationId: string, assignToUserId: string): Promise<any> {
        try {
            const result = await this.assignConversationFn({ organizationId, conversationId, assignToUserId });
            return result.data;
        } catch (error) {
            console.error('Error assigning conversation:', error);
            throw error;
        }
    }

    /**
     * Mark conversation as dealt with (archive + clear assignment + log)
     */
    async dealWithConversation(organizationId: string, conversationId: string): Promise<any> {
        try {
            const result = await this.dealWithConversationFn({ organizationId, conversationId });
            return result.data;
        } catch (error) {
            console.error('Error dealing with conversation:', error);
            throw error;
        }
    }

    /**
     * Recall a conversation to the current user
     */
    async recallConversation(organizationId: string, conversationId: string): Promise<any> {
        try {
            const result = await this.recallConversationFn({ organizationId, conversationId });
            return result.data;
        } catch (error) {
            console.error('Error recalling conversation:', error);
            throw error;
        }
    }

    /**
     * Get admin/owner users for the organization (for assignment dropdown)
     */
    async getAdminUsers(organizationId: string): Promise<{ userId: string, displayName: string, email: string, role: string }[]> {
        try {
            const result = await this.getAdminUsersFn({ organizationId });
            return result.data.users;
        } catch (error) {
            console.error('Error getting admin users:', error);
            throw error;
        }
    }

    /**
     * List campaigns / scheduled sends / automations / API sends previously
     * delivered to this contact. Scans the organisation's most recent campaigns
     * server-side; see wabGetContactMessageHistory for why this is a keyed
     * lookup rather than a query.
     */
    async getContactMessageHistory(organizationId: string, phoneNumber: string): Promise<ContactSendHistoryEntry[]> {
        const result = await this.getContactHistoryFn({ organizationId, phoneNumber });
        return result.data.entries || [];
    }

    /**
     * Clear selected conversation and tear down its listener
     */
    clearSelection(): void {
        this.selectedConversationUnsubscribe?.();
        this.selectedConversationUnsubscribe = undefined;
        this.selectedConversationSubject.next(null);
        this.teardownMessages();
    }

    /** Tear down the thread listener and reset its paging state. */
    private teardownMessages(): void {
        this.messagesUnsubscribe?.();
        this.messagesUnsubscribe = undefined;
        this.messagesContext = undefined;
        this.messagesWindowSize = ConversationService.MESSAGE_PAGE_SIZE;
        this.messagesSubject.next([]);
        this.hasOlderMessagesSubject.next(false);
        this.loadingOlderSubject.next(false);
    }

    /**
     * Tear down all active Firestore listeners
     */
    dispose(): void {
        this.conversationsUnsubscribe?.();
        this.conversationsUnsubscribe = undefined;
        this.conversationsContext = undefined;
        clearTimeout(this.countTimer);
        this.hasMoreConversationsSubject.next(false);
        this.loadingMoreConversationsSubject.next(false);
        this.conversationsTotalSubject.next(null);
        this.selectedConversationUnsubscribe?.();
        this.selectedConversationUnsubscribe = undefined;
        this.teardownMessages();
    }

    /**
     * Format time remaining in 24-hour window
     */
    formatWindowRemaining(ms: number): string {
        if (ms <= 0) return 'Expired';

        const hours = Math.floor(ms / (1000 * 60 * 60));
        const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));

        if (hours > 0) {
            return `${hours}h ${minutes}m remaining`;
        }
        return `${minutes}m remaining`;
    }

    /**
     * Continue a website conversation on WhatsApp.
     *
     * Creates a second, linked WhatsApp thread rather than moving this one —
     * see the callable for why. Refuses unless the visitor gave a mobile number
     * and agreed to be contacted.
     */
    async handoffToWhatsApp(params: {
        organizationId: string;
        conversationId: string;
        templateName: string;
        templateLanguage: string;
        phoneNumberId?: string;
        components?: unknown[];
    }): Promise<{ whatsappConversationId: string }> {
        const result = await this.handoffToWhatsAppFn(params);
        return result.data;
    }
}
